import React, { useContext, useMemo } from 'react';

import AccountContext from 'contexts/AccountContext';
import BonusPeriodSelector from 'components/Board/PeriodSelector/BonusPeriodSelector';
import Row from 'components/Common/Row';
import ResourceTopBar from 'components/ResourceTopBar';
import PermissionGates from 'components/PermissionGates';
import { BonusOverviewContext } from 'components/DriverAX/providers/BonusOverviewProvider';
import usePopup from 'hooks/usePopup';
import Button from 'kingpin/atoms/Button';
import PERMISSIONS from 'permissionDefinitions';
import EnterOverrideModeButton from './EnterOverrideModeButton';
import OverrideModeActions, { OverridesBadge } from './OverrideModeActions';
import ViewDriverAppModal from './ViewDriverAppModal';

const BonusOverviewTopBar = () => {
  const { currentMode, bonusOverview, exitRecordTaggingMode } =
    useContext(BonusOverviewContext);
  const { driverAppEmbedCarrierSlug } = useContext(AccountContext);
  const {
    isOpen: isDriverAppOpen,
    open: openDriverApp,
    close: closeDriverApp,
  } = usePopup();

  const actualOverridesCount = useMemo(() => {
    if (!bonusOverview) {
      return 0;
    }

    const { overrides } = bonusOverview;

    let count = 0;
    if (overrides?.qualifierOverride) {
      count++;
    }

    count += overrides?.criteriaOverrides.length ?? 0;
    count += bonusOverview.actualRewardOutput.rewardOverrides?.length ?? 0;

    return count;
  }, [bonusOverview]);

  const InnerContent = useMemo(() => {
    if (!bonusOverview) {
      return null;
    }

    if (currentMode.mode === 'override') {
      return <OverrideModeActions />;
    } else if (currentMode.mode === 'recordTagging') {
      return (
        <Button
          size={'Medium'}
          type={'Secondary'}
          label={'Back'}
          icon={'arrow-left'}
          onClick={exitRecordTaggingMode}
        />
      );
    } else {
      return (
        <>
          <BonusPeriodSelector />
          <Row centerAlign style={{ gap: 8 }}>
            {driverAppEmbedCarrierSlug && (
              <Button
                size={'Medium'}
                type={'Secondary'}
                label={'View Driver App'}
                onClick={openDriverApp}
                isDisabled={bonusOverview?.bonusPeriod.status === 'archived'}
                isDisabledMessage={
                  'Archived periods are not available in the driver app'
                }
              />
            )}
            <PermissionGates.Has
              requiredPermission={PERMISSIONS.DRIVER_BONUS.OVERRIDE_CATEGORIES}
            >
              <Row centerAlign>
                <OverridesBadge overridesCount={actualOverridesCount} />
                <EnterOverrideModeButton />
              </Row>
            </PermissionGates.Has>
          </Row>
        </>
      );
    }
  }, [
    bonusOverview,
    currentMode,
    exitRecordTaggingMode,
    actualOverridesCount,
    driverAppEmbedCarrierSlug,
    openDriverApp,
  ]);

  return (
    <>
      <ResourceTopBar
        style={{
          backgroundColor:
            currentMode.mode === 'override'
              ? 'var(--color-bg-tertiary)'
              : undefined,
        }}
      >
        <Row centerAlign spaceBetween style={{ width: '100%' }}>
          {InnerContent}
        </Row>
      </ResourceTopBar>
      <ViewDriverAppModal isOpen={isDriverAppOpen} close={closeDriverApp} />
    </>
  );
};

export default BonusOverviewTopBar;
